import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';

const PrivacyPage = () => {
  const { language } = useLanguage();
  
  return (
    <main className="pt-32" data-testid="privacy-page">
      <SEOHead
        title="Politique de Confidentialite | Body-Up Personal Training Nyon"
        description="Politique de confidentialite de Body-Up Personal Training, Route des Tattes d'Oie 95, 1260 Nyon, Suisse."
        path="/privacy"
        noindex={true}
      />
      <section className="py-20 bg-gradient-to-b from-black to-gray-900 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackNav />
          <AnimatedSection>
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                {language === 'fr' ? 'Politique de Confidentialité' : 'Privacy Policy'}
              </h1>
              <p className="text-gray-400 mt-4">
                Body-Up Personal Training
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {language === 'fr' ? 'Dernière mise à jour : 10/12/2025' : 'Last updated: 10/12/2025'}
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <div className="prose prose-invert max-w-none space-y-6">
              {/* Section 1 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '1. Responsable du traitement' : '1. Data Controller'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? <>Le responsable du traitement des données personnelles est <strong>Body-Up Personal Training</strong>, situé Route des Tattes d'Oie 95, 1260 Nyon, Suisse.</>
                    : <>The data controller is <strong>Body-Up Personal Training</strong>, located at Route des Tattes d'Oie 95, 1260 Nyon, Switzerland.</>
                  }
                </p>
                <p className="text-gray-300 mt-2">
                  Email: info@body-up.ch<br />
                  Tél: +41 78 604 44 23
                </p>
              </div>

              {/* Section 2 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '2. Données collectées' : '2. Data Collected'}
                </h2>
                <p className="text-gray-300 mb-4">
                  {language === 'fr' 
                    ? "Nous collectons les données personnelles suivantes :"
                    : "We collect the following personal data:"
                  }
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>{language === 'fr' ? "Nom, prénom" : "First name, last name"}</li>
                  <li>{language === 'fr' ? "Adresse email" : "Email address"}</li>
                  <li>{language === 'fr' ? "Numéro de téléphone" : "Phone number"}</li>
                  <li>{language === 'fr' ? "Adresse postale (si nécessaire)" : "Postal address (if necessary)"}</li>
                  <li>{language === 'fr' ? "Données de santé pertinentes pour votre entraînement (avec votre consentement explicite)" : "Health data relevant to your training (with your explicit consent)"}</li>
                  <li>{language === 'fr' ? "Données de composition corporelle via le scanner 3D Styku" : "Body composition data via the 3D Styku scanner"}</li>
                  <li>{language === 'fr' ? "Historique des séances et progression" : "Session history and progress"}</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '3. Finalités du traitement' : '3. Purpose of Processing'}
                </h2>
                <p className="text-gray-300 mb-4">
                  {language === 'fr' 
                    ? "Vos données sont utilisées pour :"
                    : "Your data is used to:"
                  }
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>{language === 'fr' ? "Gérer vos réservations et séances de coaching" : "Manage your bookings and coaching sessions"}</li>
                  <li>{language === 'fr' ? "Personnaliser votre programme d'entraînement" : "Personalize your training program"}</li>
                  <li>{language === 'fr' ? "Suivre votre progression et adapter vos objectifs" : "Track your progress and adapt your goals"}</li>
                  <li>{language === 'fr' ? "Vous contacter concernant vos rendez-vous" : "Contact you regarding your appointments"}</li>
                  <li>{language === 'fr' ? "Vous envoyer des informations sur nos services (avec votre consentement)" : "Send you information about our services (with your consent)"}</li>
                  <li>{language === 'fr' ? "Améliorer nos services" : "Improve our services"}</li>
                  <li>{language === 'fr' ? "Respecter nos obligations légales" : "Comply with our legal obligations"}</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '4. Base légale du traitement' : '4. Legal Basis for Processing'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? "Le traitement de vos données personnelles est fondé sur : l'exécution du contrat de coaching, votre consentement explicite (notamment pour les données de santé), nos intérêts légitimes (amélioration des services), le respect de nos obligations légales."
                    : "The processing of your personal data is based on: the execution of the coaching contract, your explicit consent (particularly for health data), our legitimate interests (service improvement), compliance with our legal obligations."
                  }
                </p>
              </div>

              {/* Section 5 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '5. Partage des données' : '5. Data Sharing'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? "Vos données personnelles ne sont jamais vendues à des tiers. Elles peuvent être partagées avec des prestataires de services (hébergement, système de réservation) uniquement dans la mesure nécessaire à la fourniture de nos services, et sous contrat de confidentialité."
                    : "Your personal data is never sold to third parties. It may be shared with service providers (hosting, booking system) only to the extent necessary to provide our services, and under confidentiality agreements."
                  }
                </p>
              </div>

              {/* Section 6 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '6. Durée de conservation' : '6. Data Retention'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? "Vos données sont conservées pendant la durée de notre relation commerciale, puis archivées pendant la durée légale de conservation (généralement 10 ans pour les données comptables). Les données de santé sont supprimées sur demande ou à la fin de notre collaboration."
                    : "Your data is retained for the duration of our business relationship, then archived for the legal retention period (generally 10 years for accounting data). Health data is deleted upon request or at the end of our collaboration."
                  }
                </p>
              </div>

              {/* Section 7 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '7. Protection des données' : '7. Data Protection'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? "Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données personnelles contre tout accès non autorisé, modification, divulgation ou destruction. L'accès à vos données est strictement limité aux personnes qui ont besoin d'y accéder dans le cadre de leurs fonctions."
                    : "We implement appropriate technical and organizational security measures to protect your personal data against unauthorized access, modification, disclosure or destruction. Access to your data is strictly limited to those who need to access it as part of their duties."
                  }
                </p>
              </div>

              {/* Section 8 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '8. Vos droits' : '8. Your Rights'}
                </h2>
                <p className="text-gray-300 mb-4">
                  {language === 'fr' 
                    ? "Conformément à la loi suisse sur la protection des données (LPD) et au RGPD, vous disposez des droits suivants :"
                    : "In accordance with the Swiss Data Protection Act (DPA) and GDPR, you have the following rights:"
                  }
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>{language === 'fr' ? "Droit d'accès à vos données personnelles" : "Right to access your personal data"}</li>
                  <li>{language === 'fr' ? "Droit de rectification des données inexactes" : "Right to rectify inaccurate data"}</li>
                  <li>{language === 'fr' ? "Droit à l'effacement (« droit à l'oubli »)" : "Right to erasure ('right to be forgotten')"}</li>
                  <li>{language === 'fr' ? "Droit à la limitation du traitement" : "Right to restriction of processing"}</li>
                  <li>{language === 'fr' ? "Droit à la portabilité des données" : "Right to data portability"}</li>
                  <li>{language === 'fr' ? "Droit d'opposition au traitement" : "Right to object to processing"}</li>
                  <li>{language === 'fr' ? "Droit de retirer votre consentement à tout moment" : "Right to withdraw your consent at any time"}</li>
                </ul>
                <p className="text-gray-300 mt-4">
                  {language === 'fr' 
                    ? "Pour exercer ces droits, contactez-nous à info@body-up.ch"
                    : "To exercise these rights, contact us at info@body-up.ch"
                  }
                </p>
              </div>

              {/* Section 9 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '9. Cookies' : '9. Cookies'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? "Notre site utilise des cookies techniques nécessaires au bon fonctionnement du site. Nous utilisons également des cookies analytiques (Google Analytics) pour comprendre comment vous utilisez notre site, avec votre consentement. Vous pouvez gérer vos préférences dans les paramètres de votre navigateur."
                    : "Our website uses technical cookies necessary for the proper functioning of the site. We also use analytical cookies (Google Analytics) to understand how you use our site, with your consent. You can manage your preferences in your browser settings."
                  }
                </p>
              </div>

              {/* Section 10 */}
              <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {language === 'fr' ? '10. Modifications' : '10. Modifications'}
                </h2>
                <p className="text-gray-300">
                  {language === 'fr' 
                    ? "Body-Up se réserve le droit de modifier la présente politique de confidentialité. La version en vigueur est celle publiée sur body-up.ch."
                    : "Body-Up reserves the right to modify this privacy policy. The current version is the one published on body-up.ch."
                  }
                </p>
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-8 border border-primary/30 text-center">
                <h3 className="text-xl font-bold text-white mb-4">
                  {language === 'fr' ? 'Des questions ?' : 'Questions?'}
                </h3>
                <p className="text-gray-300 mb-4">
                  {language === 'fr' 
                    ? "Pour toute question concernant cette politique, contactez-nous :"
                    : "For any questions regarding this policy, contact us:"
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="mailto:info@body-up.ch" className="inline-flex items-center gap-2 text-primary hover:underline">
                    <Mail className="w-4 h-4" />
                    info@body-up.ch
                  </a>
                  <a href="tel:+41786044423" className="inline-flex items-center gap-2 text-primary hover:underline">
                    <Phone className="w-4 h-4" />
                    +41 78 604 44 23
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};


// Coaching Online Landing Page

export default PrivacyPage;
